import req from '../../api/request'
import type { SnEvent } from '../../features/event/types'

/**
 * Hard stop on the page loop. Callers send `per_page: 200`, so this is 1000
 * events — far past any real schedule, and there only so a malformed
 * `total_pages` cannot spin forever.
 */
const MAX_PAGES = 5

/**
 * Follows an event list endpoint's pagination to the last page and returns one
 * complete list.
 *
 * FULLY PAGINATED on purpose. Every consumer treats events as complete — most
 * of all `clampEventsToLoadedGames`, which withholds events past the last
 * loaded game and lifts that boundary once games run out. A truncated list
 * would make it lift over events that were never fetched, silently dropping
 * them. Games can paginate lazily because they own the scroll; events cannot.
 *
 * Shared by the list endpoints that page (`/events/membership`,
 * `/schedules/{id}/events`) so the two can never drift in how far they read.
 *
 * Errors throw, like every other event service.
 */
export const fetchAllEventPages = async <TQuery extends object>(
  path: string,
  query: TQuery = {} as TQuery
): Promise<SnEvent[]> => {
  const all: SnEvent[] = []
  let page = 1
  // Counts REQUESTS, not page numbers. `page` is derived from the response, so
  // a server that always reports `current_page: 1` would reset it every time
  // and a `page >= MAX_PAGES` guard would never fire. This one always advances.
  let requests = 0

  for (;;) {
    requests += 1
    const response = await req(path, {
      query: { ...query, page },
    })

    // Accept both the documented envelope and a bare array — the other event
    // list endpoints return each, so this tolerates whichever ships.
    const isBare = Array.isArray(response)
    const events = (isBare ? response : response?.data) ?? []
    all.push(...(events as SnEvent[]))

    // A bare array carries no pagination, so it is the whole answer.
    if (isBare) {
      break
    }

    const pagination = response?.meta?.pagination
    const currentPage = Number(pagination?.current_page ?? page)
    const totalPages = Number(pagination?.total_pages ?? 1)

    // `currentPage < totalPages` rather than a page counter: trusting the
    // response's own view of where it is means a server that ignores `page`
    // ends the loop instead of refetching page one forever.
    const next = currentPage + 1
    if (
      !Number.isFinite(totalPages) ||
      currentPage >= totalPages ||
      // The response did not move forward — asking again would repeat it.
      next <= page ||
      requests >= MAX_PAGES
    ) {
      break
    }
    page = next
  }

  return all
}
